import { Drawer, Tag } from '@billy/design-system'
import { useTranslation } from 'react-i18next'

import { ExternalInvoiceFromBilly } from '@features/invoices/types/externalInvoiceFromBilly'
import { mapBillyExternalInvoiceToInvoice } from '@features/invoices/utils/mapBillyExternalInvoiceToInvoice'
import { invoiceStatusToTagColor } from '@features/invoicesLegacy/constants/invoiceStatusToTagColor'
import { invoiceStatusToTitleKey } from '@features/invoicesLegacy/constants/invoiceStatusToTitleKey'
import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import { getInvoiceStatus } from '@features/invoicesLegacy/utils/getInvoiceStatus'

import { useTransactionDrawerContext } from '../../contexts/TransactionDrawerContext'
import { BookingsSection } from '../../elements/BookingsSection/BookingsSection'
import { DescriptionSection } from '../../elements/DescriptionSection'
import { DetailsSection } from '../../elements/DetailsSection'
import { HeroSection } from '../../elements/HeroSection'
import { Separator } from '../../elements/Separator'

interface ExternalInvoiceViewProps {
  invoice: ExternalInvoiceFromBilly
}

export const ExternalInvoiceView = ({ invoice }: ExternalInvoiceViewProps) => {
  const { t } = useTranslation()
  const { transactionNo } = useTransactionDrawerContext()

  const {
    amount,
    balance,
    balanceModifiers,
    contactName,
    createdTime,
    currencyId,
    dueDate,
    entryDate,
    grossAmount,
    invoiceNo,
    isPaid,
    referredCreditnoteId,
    state,
    tax,
    type,
  } = mapBillyExternalInvoiceToInvoice(invoice)

  const invoiceStatus = getInvoiceStatus({
    amount,
    balance,
    dueDate: dueDate ? new Date(dueDate) : undefined,
    isPaid,
    referredCreditnoteId,
    state,
    tax,
  })

  const isCreditNote = type === InvoiceType.CreditNote

  const statusTag = isCreditNote
    ? undefined
    : {
        color: invoiceStatusToTagColor[invoiceStatus],
        label: t(invoiceStatusToTitleKey[invoiceStatus]),
      }

  const paymentDate = balanceModifiers?.[0]?.entryDate
  const additionalTag = isCreditNote ? <Tag type="subtle">{t('credit_note')}</Tag> : null

  return (
    <>
      <Drawer.Header closable title={t('ledger.drawer_title', { number: transactionNo })} />
      <Drawer.Body withPadding={false} gap="0">
        <HeroSection
          additionalTag={additionalTag}
          amount={grossAmount}
          contactName={contactName ?? undefined}
          currency={currencyId}
          entryDate={entryDate}
          source={t('ledger.invoice_no', { number: invoiceNo })}
          status={statusTag}
        />
        <Separator />
        <BookingsSection originatorCurrency={currencyId} />
        <Separator />
        <DescriptionSection />
        <Separator />
        <DetailsSection number={invoiceNo} createdTime={createdTime} paymentDate={paymentDate} />
        <Separator />
      </Drawer.Body>
    </>
  )
}
